import { MapPin, Navigation2, Car } from "lucide-react";
import { useContent } from "../context/ContentContext";

export const Location = () => {
  const { content } = useContent();
  const directions = content.location_directions || [];
  const rooms = content.hotel_rooms || [];

  return (
    <section
      id="location"
      data-testid="section-location"
      className="py-24 md:py-36 bg-sage-100/50"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-sage-500 uppercase tracking-[0.35em] text-xs mb-6">
              {content.location_eyebrow}
            </p>
            <h2
              data-testid="location-title"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-sage-800 mb-6"
            >
              {content.location_title_prefix}{" "}
              <span className="font-script text-sage-500">
                {content.location_title_script}
              </span>
            </h2>
            <p className="text-sage-700 text-base md:text-lg leading-relaxed mb-8">
              {content.location_description}
            </p>

            <div className="space-y-4 mb-10">
              <div className="flex items-start gap-4" data-testid="location-address">
                <MapPin className="text-sage-500 shrink-0 mt-1" size={22} />
                <div>
                  <div className="font-medium text-sage-800">Gut Haferkorn</div>
                  <div className="text-sage-600 text-sm">Nähe Leisnig, Sachsen</div>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="location-date">
                <Navigation2 className="text-sage-500 shrink-0 mt-1" size={22} />
                <div>
                  <div className="font-medium text-sage-800">{content.location_date_main}</div>
                  <div className="text-sage-600 text-sm">{content.location_date_sub}</div>
                </div>
              </div>
            </div>

            <div
              data-testid="location-directions"
              className="rounded-2xl border border-sage-200 bg-white p-6 md:p-8 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-4">
                <Car className="text-sage-500" size={22} />
                <h3 className="font-serif text-2xl text-sage-800">
                  {content.location_directions_title}
                </h3>
              </div>
              <ol className="space-y-3 text-sage-700 text-sm md:text-base">
                {directions.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="font-serif text-sage-500 text-lg">{i + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              <a
                data-testid="location-maps-link"
                href={content.location_maps_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 text-white px-6 py-3 text-sm uppercase tracking-[0.2em] transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-lg"
              >
                In Google Maps öffnen
              </a>
            </div>
          </div>

          <div className="relative">
            <img
              data-testid="location-image"
              src={content.location_image}
              alt="Gut Haferkorn - Location"
              className="w-full h-[420px] md:h-[640px] object-cover rounded-2xl shadow-xl"
            />
            <div className="absolute -bottom-6 -left-6 hidden md:flex flex-col items-start bg-white px-6 py-4 rounded-2xl shadow-lg border border-sage-200">
              <span className="font-script text-2xl text-sage-500">01.08.2026</span>
              <span className="text-sage-700 text-xs uppercase tracking-[0.25em]">Save the Date</span>
            </div>
          </div>
        </div>

        {/* Hotel / Übernachtung */}
        <div
          data-testid="location-hotel"
          className="mt-20 lg:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
        >
          <div className="lg:col-span-5">
            <p className="text-sage-500 uppercase tracking-[0.35em] text-xs mb-6">
              {content.hotel_eyebrow}
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-sage-800 mb-6">
              {content.hotel_title_prefix}{" "}
              <span className="font-script text-sage-500">
                {content.hotel_title_script}
              </span>
            </h3>
            <p className="text-sage-700 text-base md:text-lg leading-relaxed">
              {content.hotel_description}
            </p>
            <p className="mt-4 text-sm text-sage-600">{content.hotel_details}</p>
            <p className="mt-6 text-sage-700 text-sm">{content.hotel_cta}</p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {rooms.map((r, i) => (
              <div
                key={`${r.name}-${i}`}
                data-testid={`location-room-${i}`}
                className="bg-white rounded-2xl border border-sage-200 p-5 md:p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <h4 className="font-serif text-xl text-sage-800 leading-tight">{r.name}</h4>
                  <span className="text-sage-500 font-serif text-2xl whitespace-nowrap">
                    {r.price}
                  </span>
                </div>
                <p className="text-xs text-sage-600">{r.detail}</p>
                <p className="mt-1 text-[11px] text-sage-400 uppercase tracking-wide">
                  {content.hotel_rooms_footnote}
                </p>
              </div>
            ))}
            {content.hotel_link_url && (
              <a
                data-testid="location-hotel-link"
                href={content.hotel_link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full border border-sage-400 text-sage-700 hover:bg-sage-500 hover:text-white hover:border-sage-500 px-6 py-3 text-sm uppercase tracking-[0.2em] transition-all duration-300 bg-white"
              >
                {content.hotel_link_label}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Location;
